import { proStudioUrqlGql as urqlGql } from '@pypestream/api-services';
import { throwErrorWithId } from '@pypestream/utils';

import {
  buildSelectedSolution,
  checkForErrors,
  readVersionNames,
} from '../../utils';
import {
  ProStudioContext,
  ProStudioEvents,
  SelectedSolution,
} from '../prostudio.xstate.types';
import { tableStore } from '../../stores';

export const setSelectedSolution = async (
  ctx: ProStudioContext,
  event: ProStudioEvents
): Promise<{
  selectedSolution: SelectedSolution;
  cb?: (path?: string) => void;
} | void> => {
  if (event.type !== 'selectBot') {
    return;
  }

  const { project, solutions } = ctx;
  const { name, cb } = event;

  const projectId = project?.id;

  const selectedSolution_ = solutions?.find(
    (solution) => solution.displayName === name
  );

  // Matched on the solution uuid, not the old `botSolutionId`: v2 leaves that null on
  // every new solution, so requiring it rejected solutions that were perfectly fine.
  if (!selectedSolution_?.id) {
    throwErrorWithId('Selected solution ID is required');
    return;
  }

  if (!projectId) {
    throwErrorWithId('Project ID is required');
    return;
  }

  // The solutions list query stays light and does not carry versions, so they are
  // fetched here for the one solution being opened.
  const versionsResponse = await urqlGql.getSolutionVersions({
    solutionId: selectedSolution_.id,
  });

  checkForErrors(versionsResponse);

  const solutionVersions =
    versionsResponse.data?.admin_?.solutionVersions ?? [];
  const versions = readVersionNames(
    solutionVersions,
    selectedSolution_.defaultDraftVersionId
  );
  const draftVersion = versions[versions.length - 1];

  // Media reads `selectedBot.assets`, which used to come from Bot Manager's getBotById.
  // It now comes from `assetsFiles`, scoped to the draft version. A solution with no
  // versions yet simply has no media.
  let assets: { id: string; url: string }[] = [];

  if (draftVersion) {
    const assetsResponse = await urqlGql.getAssetsFiles({
      solutionId: selectedSolution_.id,
      versionName: draftVersion,
    });

    checkForErrors(assetsResponse);

    assets = (assetsResponse.data?.admin_?.assetsFiles ?? [])
      .filter((file) => file?.url)
      .map((file) => ({
        // `sha` is the stable per-content id; `path` is the fallback so a row always
        // has a key even if the backend omits it.
        id: file?.sha || file?.path || '',
        url: file?.url as string,
      }));
  }

  const selectedSolution = buildSelectedSolution({
    solution: { ...selectedSolution_, solutionVersions },
    displayName: name,
    assets,
  });

  tableStore.setErrors([]);

  return {
    selectedSolution,
    cb: cb ? () => cb(`/${projectId}/${selectedSolution.uniqueId}`) : undefined,
  };
};
